import {
  getAccessToken,
  removeAccessToken,
  removeRefreshToken,
  saveAccessToken,
  saveRefreshToken,
  saveUserData,
} from '../utils/storage';
import { authFetch, getApiUrl } from '../utils/api';
import { friendlyApiError } from '../utils/apiErrors';

const LIVE_ALLOWED_MODULES_KEY = 'live_ai_allowed_modules';
const LIVE_PERMISSIONS_KEY = 'live_ai_permissions';
const LIVE_SESSION_KEY = 'live_ai_session';
const LIVE_PERMISSION_PREVIEW_KEY = 'live_ai_permission_preview';
const TOKEN_HANDOFF_KEYS = ['authToken', 'accessToken', 'refreshToken'];
const LIVE_SESSION_FETCH_TIMEOUT_MS = 15000;

export interface LiveDashboardAction {
  action: string;
  permissionKey?: string;
}

export interface LiveDashboardReport {
  reportKey: string;
  label: string;
  displayOrder?: number;
  actions: string[];
  permissionKeys?: string[];
}

export interface LiveDashboardModule {
  moduleKey: string;
  label: string;
  displayOrder?: number;
  reports: LiveDashboardReport[];
}

export interface LiveDashboardSessionResponse {
  success: boolean;
  user: {
    username: string;
    displayName: string;
  };
  externalPermissions: string[];
  permissions: string[];
  allowedModules: LiveDashboardModule[];
  session: {
    sessionId: string;
    expiresAt: string;
    createdAt?: string;
    validationMode?: string;
  };
  accessToken?: string;
  tokenType?: string;
  refreshToken?: string;
}

export interface LiveLoginRequest {
  authToken: string;
  refreshToken?: string;
}

export interface LiveLoginLinkResponse {
  success: boolean;
  frontendUrl: string;
}

const makeHeaders = (): Record<string, string> => {
  const token = getAccessToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

export const buildLiveDashboardRedirectUrl = (
  frontendUrl: string,
  request: LiveLoginRequest,
): string => {
  const url = new URL(frontendUrl, window.location.origin);
  const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
  hashParams.set('authToken', request.authToken);
  if (request.refreshToken) {
    hashParams.set('refreshToken', request.refreshToken);
  }
  url.hash = hashParams.toString();
  return url.toString();
};

const setJsonStorage = (key: string, value: unknown): void => {
  const serialized = JSON.stringify(value);
  sessionStorage.setItem(key, serialized);
  localStorage.setItem(key, serialized);
};

const removeStorage = (key: string): void => {
  sessionStorage.removeItem(key);
  localStorage.removeItem(key);
};

const readJsonStorage = <T,>(key: string): T | null => {
  const stored = sessionStorage.getItem(key) || localStorage.getItem(key);
  if (!stored) {
    return null;
  }

  try {
    return JSON.parse(stored) as T;
  } catch {
    return null;
  }
};

export const saveLiveDashboardSession = (payload: LiveDashboardSessionResponse): void => {
  if (payload.accessToken) {
    saveAccessToken(payload.accessToken);
  }
  // The backend returns the current (possibly rotated) Rage refresh token -
  // keep it so the next refresh uses the valid one.
  if (payload.refreshToken) {
    saveRefreshToken(payload.refreshToken);
  }

  saveUserData({
    user_id: payload.user.username,
    id: payload.user.username,
    name: payload.user.displayName || payload.user.username,
    email: '',
    status: 'active',
    onboarding_status: 'COMPLETED',
  });

  setJsonStorage(LIVE_ALLOWED_MODULES_KEY, payload.allowedModules || []);
  setJsonStorage(LIVE_PERMISSIONS_KEY, payload.permissions || []);
  setJsonStorage(LIVE_SESSION_KEY, payload.session || null);
  removeStorage(LIVE_PERMISSION_PREVIEW_KEY);
};

export const clearLiveDashboardSession = (): void => {
  removeStorage(LIVE_ALLOWED_MODULES_KEY);
  removeStorage(LIVE_PERMISSIONS_KEY);
  removeStorage(LIVE_SESSION_KEY);
  removeStorage(LIVE_PERMISSION_PREVIEW_KEY);
  removeAccessToken();
  removeRefreshToken();
};

export const consumeLiveDashboardTokenHandoff = (): boolean => {
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const queryParams = new URLSearchParams(window.location.search);
  const authToken = (
    hashParams.get('authToken')
    || hashParams.get('accessToken')
    || queryParams.get('authToken')
    || queryParams.get('accessToken')
    || ''
  ).trim();
  const refreshToken = (
    hashParams.get('refreshToken')
    || queryParams.get('refreshToken')
    || ''
  ).trim();

  if (!authToken && !refreshToken) {
    return false;
  }

  clearLiveDashboardSession();
  if (authToken) {
    saveAccessToken(authToken);
  }
  if (refreshToken) {
    saveRefreshToken(refreshToken);
  }

  TOKEN_HANDOFF_KEYS.forEach((key) => queryParams.delete(key));
  const nextSearch = queryParams.toString();
  const cleanUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}`;
  window.history.replaceState({}, document.title, cleanUrl);
  return true;
};

export const getStoredLiveAllowedModules = (): LiveDashboardModule[] | null => {
  const parsed = readJsonStorage<LiveDashboardModule[]>(LIVE_ALLOWED_MODULES_KEY);
  return Array.isArray(parsed) ? parsed : null;
};

export const getStoredLivePermissions = (): string[] | null => {
  const parsed = readJsonStorage<string[]>(LIVE_PERMISSIONS_KEY);
  return Array.isArray(parsed) ? parsed : null;
};

export const isLocalPermissionPreviewEnabled = (): boolean => {
  return sessionStorage.getItem(LIVE_PERMISSION_PREVIEW_KEY) === 'true'
    || localStorage.getItem(LIVE_PERMISSION_PREVIEW_KEY) === 'true';
};

export const enableFullPermissionPreview = (): void => {
  const permissions = [
    'store_performance.report:view',
    'store_performance.segment:view',
    'forecast.report:view',
    'new_stores.report:view',
    'lms_master.branch_report:view',
    'lms_master.store_marketing_attributes:view',
    'lms_master.store_attributes:view',
    'lms_recon.progress_report:view',
    'lms_recon.rental_recon_summary:view',
    'lms_recon.utilities_vs_rent:view',
    'lms_recon.invoiced_vs_paid_rent:view',
    'lms_recon.invoiced_vs_paid_utilities:view',
    'lms_recon.contractual_charges:view',
    'lms_recon.variable_charges:view',
    'lms_recon.escalations_rent:view',
    'lms_recon.escalations_other:view',
    'lms_recon.growth_rent:view',
    'lms_recon.growth_utilities:view',
    'lms_recon.total_rate_per_sqm:view',
    'lms_recon.rental_monthend_batch_report:view',
    'lms_recon.rental_arrears_batch_report:view',
    'lms_recon.municipal_monthend_batch_report:view',
    'lms_recon.municipal_arrears_batch_report:view',
    'lms_recon.transaction_report:view',
    'debt_review.account_access:view',
    'debt_review.application_users:view',
  ];

  const allowedModules: LiveDashboardModule[] = [
    {
      moduleKey: 'lms_recon_analysis',
      label: 'LMS Recon Analysis',
      displayOrder: 10,
      reports: [
        { reportKey: 'progress-report', label: 'Progress Report', displayOrder: 10, actions: ['view'], permissionKeys: ['lms_recon.progress_report:view'] },
        { reportKey: 'rental-recon-summary', label: 'Rental Recon Summary', displayOrder: 20, actions: ['view'], permissionKeys: ['lms_recon.rental_recon_summary:view'] },
        { reportKey: 'utilities-vs-rent', label: 'Utilities vs. Rent', displayOrder: 30, actions: ['view'], permissionKeys: ['lms_recon.utilities_vs_rent:view'] },
        { reportKey: 'invoiced-vs-paid-rent', label: 'Invoiced vs. Paid - Rent', displayOrder: 40, actions: ['view'], permissionKeys: ['lms_recon.invoiced_vs_paid_rent:view'] },
        { reportKey: 'invoiced-vs-paid-utilities', label: 'Invoiced vs. Paid - Utilities', displayOrder: 50, actions: ['view'], permissionKeys: ['lms_recon.invoiced_vs_paid_utilities:view'] },
        { reportKey: 'contractual-charges', label: 'Contractual Charges', displayOrder: 60, actions: ['view'], permissionKeys: ['lms_recon.contractual_charges:view'] },
        { reportKey: 'variable-charges', label: 'Variable Charges', displayOrder: 70, actions: ['view'], permissionKeys: ['lms_recon.variable_charges:view'] },
        { reportKey: 'escalations-rent', label: 'Escalations - Rent', displayOrder: 80, actions: ['view'], permissionKeys: ['lms_recon.escalations_rent:view'] },
        { reportKey: 'escalations-other', label: 'Escalations - Other', displayOrder: 90, actions: ['view'], permissionKeys: ['lms_recon.escalations_other:view'] },
        { reportKey: 'growth-rent', label: 'Growth - Rent', displayOrder: 100, actions: ['view'], permissionKeys: ['lms_recon.growth_rent:view'] },
        { reportKey: 'growth-utilities', label: 'Growth - Utilities', displayOrder: 110, actions: ['view'], permissionKeys: ['lms_recon.growth_utilities:view'] },
        { reportKey: 'total-rate-per-sqm', label: 'Total Rate per SQM', displayOrder: 120, actions: ['view'], permissionKeys: ['lms_recon.total_rate_per_sqm:view'] },
        { reportKey: 'rental-monthend-batch-report', label: 'Rental Monthend Batch Report', displayOrder: 130, actions: ['view'], permissionKeys: ['lms_recon.rental_monthend_batch_report:view'] },
        { reportKey: 'rental-arrears-batch-report', label: 'Rental Arrears Batch Report', displayOrder: 140, actions: ['view'], permissionKeys: ['lms_recon.rental_arrears_batch_report:view'] },
        { reportKey: 'municipal-monthend-batch-report', label: 'Municipal Monthend Batch Report', displayOrder: 150, actions: ['view'], permissionKeys: ['lms_recon.municipal_monthend_batch_report:view'] },
        { reportKey: 'municipal-arrears-batch-report', label: 'Municipal Arrears Batch Report', displayOrder: 160, actions: ['view'], permissionKeys: ['lms_recon.municipal_arrears_batch_report:view'] },
        { reportKey: 'transaction-report', label: 'Transaction Report', displayOrder: 170, actions: ['view'], permissionKeys: ['lms_recon.transaction_report:view'] },
      ],
    },
    {
      moduleKey: 'store_performance',
      label: 'LMS Store Analytics V2',
      displayOrder: 20,
      reports: [
        { reportKey: 'store_performance_report', label: 'Store Performance Report', displayOrder: 10, actions: ['view'], permissionKeys: ['store_performance.report:view'] },
        { reportKey: 'segment_report', label: 'Segment Report', displayOrder: 20, actions: ['view'], permissionKeys: ['store_performance.segment:view'] },
        { reportKey: 'forecast_report', label: 'Forecast Report', displayOrder: 30, actions: ['view'], permissionKeys: ['forecast.report:view'] },
        { reportKey: 'new_stores_report', label: 'New Stores Report', displayOrder: 40, actions: ['view'], permissionKeys: ['new_stores.report:view'] },
      ],
    },
    {
      moduleKey: 'lms_master_report',
      label: 'LMS Master Report',
      displayOrder: 30,
      reports: [
        { reportKey: 'branch_report', label: 'Branch Report', displayOrder: 10, actions: ['view'], permissionKeys: ['lms_master.branch_report:view'] },
        { reportKey: 'store_marketing_attributes', label: 'Store Marketing Attributes', displayOrder: 20, actions: ['view'], permissionKeys: ['lms_master.store_marketing_attributes:view'] },
        { reportKey: 'store_attributes', label: 'Store Attributes', displayOrder: 30, actions: ['view'], permissionKeys: ['lms_master.store_attributes:view'] },
      ],
    },
    {
      moduleKey: 'debt_review',
      label: 'Debt Review',
      displayOrder: 40,
      reports: [
        { reportKey: 'account_access', label: 'Account Access', displayOrder: 10, actions: ['view'], permissionKeys: ['debt_review.account_access:view'] },
        { reportKey: 'application_users', label: 'Application Users', displayOrder: 20, actions: ['view'], permissionKeys: ['debt_review.application_users:view'] },
      ],
    },
    {
      moduleKey: 'vacancy_report',
      label: 'Vacancy Report',
      displayOrder: 50,
      reports: [
        { reportKey: 'planned_vs_actual', label: 'Planned vs. Actual', displayOrder: 10, actions: ['view'], permissionKeys: ['vacancy_report.planned_vs_actual:view'] },
        { reportKey: 'vacancies', label: 'Vacancies', displayOrder: 20, actions: ['view'], permissionKeys: ['vacancy_report.vacancies:view'] },
        { reportKey: 'overstaffing', label: 'Overstaffing', displayOrder: 30, actions: ['view'], permissionKeys: ['vacancy_report.overstaffing:view'] },
      ],
    },
  ];

  setJsonStorage(LIVE_ALLOWED_MODULES_KEY, allowedModules);
  setJsonStorage(LIVE_PERMISSIONS_KEY, permissions);
  setJsonStorage(LIVE_SESSION_KEY, {
    sessionId: 'local-permission-preview',
    expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
    validationMode: 'local_permission_preview',
  });
  sessionStorage.setItem(LIVE_PERMISSION_PREVIEW_KEY, 'true');
  localStorage.setItem(LIVE_PERMISSION_PREVIEW_KEY, 'true');
};

export const liveDashboardLogin = async (
  request: LiveLoginRequest,
): Promise<LiveDashboardSessionResponse | LiveLoginLinkResponse> => {
  clearLiveDashboardSession();

  const response = await fetch(getApiUrl('/v1/embed-auth/live-login'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(friendlyApiError(response.status, errorData.detail, 'The dashboard session'));
  }

  const data: LiveDashboardSessionResponse | LiveLoginLinkResponse = await response.json();
  if ('allowedModules' in data) {
    saveLiveDashboardSession(data);
  } else if (data.success) {
    saveAccessToken(request.authToken);
    if (request.refreshToken) {
      saveRefreshToken(request.refreshToken);
    }
  }
  return data;
};

export const fetchLiveDashboardSession = async (): Promise<LiveDashboardSessionResponse> => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), LIVE_SESSION_FETCH_TIMEOUT_MS);

  let response: Response;
  try {
    response = await authFetch(getApiUrl('/v1/embed-auth/me'), {
      method: 'GET',
      headers: makeHeaders(),
      credentials: 'include',
      signal: controller.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new Error('Live dashboard session validation timed out');
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(friendlyApiError(response.status, errorData.detail, 'The dashboard session'));
  }

  const data: LiveDashboardSessionResponse = await response.json();
  saveLiveDashboardSession(data);
  return data;
};

export const logoutLiveDashboardSession = async (): Promise<void> => {
  await authFetch(getApiUrl('/v1/embed-auth/logout'), {
    method: 'POST',
    headers: makeHeaders(),
    credentials: 'include',
    body: JSON.stringify({}),
  }).catch(() => undefined);

  clearLiveDashboardSession();
};
